<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show"
        class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
        @click.self="closeModal">
        <div class="bg-white rounded-xl w-full max-w-lg shadow-2xl">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-user-plus text-primary text-sm"></i>
            </div>
            <div class="flex-1">
              <h3 class="text-base font-bold text-gray-900">Nouveau fournisseur</h3>
              <p class="text-[11px] text-gray-400">Renseignez les informations du fournisseur</p>
            </div>
            <button @click="closeModal"
              class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <!-- Body -->
          <form @submit.prevent="submitForm" class="px-6 pb-6">
            <!-- Nom -->
            <div class="mb-4">
              <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Nom du fournisseur <span class="text-red-400">*</span>
              </label>
              <input
                v-model.trim="form.name"
                type="text"
                required
                placeholder="Nom complet"
                :class="[
                  'w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 transition-colors',
                  validationErrors.name ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-gray-200'
                ]"
              />
              <p v-if="validationErrors.name" class="text-[11px] text-red-500 mt-1">{{ validationErrors.name[0] }}</p>
            </div>

            <!-- Email + Téléphone -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                <input
                  v-model.trim="form.email"
                  type="email"
                  placeholder="exemple@domaine.com"
                  :class="[
                    'w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 transition-colors',
                    validationErrors.email ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-gray-200'
                  ]"
                />
                <p v-if="validationErrors.email" class="text-[11px] text-red-500 mt-1">{{ validationErrors.email[0] }}</p>
              </div>
              <div>
                <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Téléphone</label>
                <input
                  v-model.trim="form.phone"
                  type="text"
                  placeholder="+221 77 000 00 00"
                  :class="[
                    'w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 transition-colors',
                    validationErrors.phone ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-gray-200'
                  ]"
                />
                <p v-if="validationErrors.phone" class="text-[11px] text-red-500 mt-1">{{ validationErrors.phone[0] }}</p>
              </div>
            </div>

            <!-- Personne de contact -->
            <div class="mb-4">
              <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Personne de contact</label>
              <input
                v-model.trim="form.contact_person"
                type="text"
                placeholder="Nom de la personne"
                :class="[
                  'w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 transition-colors',
                  validationErrors.contact_person ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-gray-200'
                ]"
              />
              <p v-if="validationErrors.contact_person" class="text-[11px] text-red-500 mt-1">{{ validationErrors.contact_person[0] }}</p>
            </div>

            <!-- Actif -->
            <div class="mb-5">
              <label class="inline-flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  v-model="form.active"
                  class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30"
                />
                <span class="text-sm text-gray-700 font-medium">Actif</span>
              </label>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4 border-t border-gray-100">
              <button type="button"
                @click="closeModal"
                :disabled="isSubmitting"
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50">
                Annuler
              </button>
              <button type="submit"
                :disabled="isSubmitting"
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-1.5"></span>
                <i v-else class="fas fa-check text-[10px] mr-1"></i>
                {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, watch } from 'vue'
import supplierService from '@/services/supplierService'
import { useToastStore } from '@/store/modules/toastStore'

export default {
    name: 'CreateSupplierModal',
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'created'],
    setup(props, { emit }) {
        const toastStore = useToastStore()

        const form = ref({
            name: '',
            email: '',
            phone: '',
            contact_person: '',
            active: true
        })
        const validationErrors = ref({})
        const isSubmitting = ref(false)

        const resetForm = () => {
            form.value = {
                name: '',
                email: '',
                phone: '',
                contact_person: '',
                active: true
            }
            validationErrors.value = {}
        }

        const closeModal = () => {
            if (!isSubmitting.value) {
                resetForm()
                emit('close')
            }
        }

        const submitForm = async () => {
            if (isSubmitting.value) return
            validationErrors.value = {}
            isSubmitting.value = true
            try {
                const payload = {
                    name: form.value.name,
                    email: form.value.email || null,
                    phone: form.value.phone || null,
                    contact_person: form.value.contact_person || null,
                    active: !!form.value.active
                }
                const response = await supplierService.createSupplier(payload)
                toastStore.addToast({
                    type: 'success',
                    message: 'Fournisseur créé avec succès',
                    duration: 4000
                })
                resetForm()
                emit('created', response.data?.data || response.data)
                emit('close')
            } catch (error) {
                if (error && error.status === 422 && error.errors) {
                    validationErrors.value = error.errors
                    toastStore.addToast({
                        type: 'error',
                        message: error.message || 'Erreurs de validation',
                        duration: 4000
                    })
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: error?.message || 'Erreur lors de la création du fournisseur',
                        duration: 4000
                    })
                }
            } finally {
                isSubmitting.value = false
            }
        }

        watch(() => props.show, (val) => {
            if (val) resetForm()
        })

        return {
            form,
            validationErrors,
            isSubmitting,
            closeModal,
            submitForm
        }
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
    transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
.modal-enter-from > div {
    transform: scale(0.95);
}
.modal-leave-to > div {
    transform: scale(0.95);
}
</style>
